<template>

    <div>
        <div v-for="question in active_questions" :key="question.id" class="mb-4 text-gray-700 dark:text-gray-400">
          <div v-if="question.question.question_type === 'hidden'" style="display:none">
            <input type="hidden" :name="question.id" :value="question.question.question_details && question.question.question_details.default ? question.question.question_details.default : ''" />
          </div>

          <div v-if="question.question.question_type === 'one_line'">
            <label class="block text-sm font-medium mb-1">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></label>
            <input type="text" :name="question.id"
              :placeholder="question.question.question_details?.placeholder || question.question.title"
              :required="question.required"
              :minlength="question.question.question_details?.min_length || undefined"
              :maxlength="question.question.question_details?.max_length || undefined"
              class="w-full border-b border-gray-300 py-2 text-sm focus:outline-none focus:border-blue-500 bg-transparent" />
            <p v-if="question.question.question_details?.min_length" class="text-[11px] text-gray-400 mt-0.5">
              Minimum {{ question.question.question_details.min_length }} characters
            </p>
          </div>

          <div v-if="question.question.question_type === 'phone_number'">
            <label class="block text-sm font-medium mb-1">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></label>
            <input type="tel" :name="question.id"
              :placeholder="question.question.question_details?.placeholder || question.question.title"
              :required="question.required"
              class="w-full border-b border-gray-300 py-2 text-sm focus:outline-none focus:border-blue-500 bg-transparent" />
          </div>

          <div v-if="question.question.question_type === 'email'">
            <label class="block text-sm font-medium mb-1">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></label>
            <input type="email" :name="question.id"
              :placeholder="question.question.question_details?.placeholder || question.question.title"
              :required="question.required"
              class="w-full border-b border-gray-300 py-2 text-sm focus:outline-none focus:border-blue-500 bg-transparent" />
          </div>

          <div v-if="question.question.question_type === 'number'">
            <label class="block text-sm font-medium mb-1">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></label>
            <input type="number" :name="question.id"
              :placeholder="question.question.question_details?.placeholder || question.question.title"
              :required="question.required"
              :min="question.question.question_details && question.question.question_details.min ? question.question.question_details.min : undefined"
              :max="question.question.question_details && question.question.question_details.max ? question.question.question_details.max : undefined"
              class="w-full border-b border-gray-300 py-2 text-sm focus:outline-none focus:border-blue-500 bg-transparent" />
          </div>

          <div v-if="question.question.question_type === 'boolean'">
            <p class="text-sm font-medium mb-2">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></p>
            <div class="flex items-center gap-5">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" :name="question.id" value="yes" :required="question.required" class="focus:ring-0" />
                <span class="text-sm opacity-80">Yes</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="radio" :name="question.id" value="no" class="focus:ring-0" />
                <span class="text-sm opacity-80">No</span>
              </label>
            </div>
          </div>

          <div v-if="question.question.question_type === 'url'">
            <label class="block text-sm font-medium mb-1">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></label>
            <input type="url" :name="question.id"
              :placeholder="question.question.question_details?.placeholder || 'https://'"
              :required="question.required"
              class="w-full border-b border-gray-300 py-2 text-sm focus:outline-none focus:border-blue-500 bg-transparent" />
          </div>

          <div v-if="question.question.question_type === 'rating'">
            <p class="text-sm font-medium mb-2">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></p>
            <div v-if="(question.question.question_details && question.question.question_details.scale || '5').toString() === '5'" class="flex gap-1">
              <button v-for="i in 5" :key="i" type="button" @click="setRating(question.id, i)"
                class="transition-transform active:scale-90">
                <svg class="w-8 h-8 transition-colors" :class="Number(ratings[question.id]) >= i ? 'text-yellow-400' : 'text-gray-200'" fill="currentColor" viewBox="0 0 576 512">
                  <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
                </svg>
              </button>
            </div>
            <div v-else class="flex flex-wrap gap-1.5">
              <button v-for="i in 11" :key="i" type="button" @click="setRating(question.id, i - 1)"
                class="w-9 h-9 rounded-lg text-sm font-semibold border transition-colors"
                :class="ratings[question.id] === (i - 1) ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-200 text-gray-500 hover:border-blue-400'">
                {{ i - 1 }}
              </button>
            </div>
            <input type="hidden" :name="question.id" :value="ratings[question.id] !== undefined ? String(ratings[question.id]) : ''" :required="question.required" />
          </div>

          <div v-if="question.question.question_type === 'file_upload'">
            <label class="block text-sm font-medium mb-1">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></label>
            <input type="file" :name="question.id" :required="question.required && !uploadedFiles[question.id]"
              :accept="buildAccept(question.question.question_details)"
              @change="e => handleFileUpload(e, question.id, question.question.question_details)"
              class="w-full text-sm text-gray-500 border border-gray-200 rounded-lg p-2 cursor-pointer" />
            <p v-if="uploadErrors[question.id]" class="text-xs text-red-500 mt-1">{{ uploadErrors[question.id] }}</p>
            <input v-if="uploadedFiles[question.id]" type="hidden" :name="question.id + '_signed_id'" :value="uploadedFiles[question.id]" />
          </div>

          <div v-if="question.question.question_type === 'date'">
            <label class="block text-sm font-medium mb-1">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></label>
            <input type="date" :name="question.id" :required="question.required"
              :min="question.question.question_details && question.question.question_details.min_date ? question.question.question_details.min_date : undefined"
              :max="question.question.question_details && question.question.question_details.max_date ? question.question.question_details.max_date : undefined"
              class="w-full border-b border-gray-300 py-2 text-sm focus:outline-none focus:border-blue-500 bg-transparent" />
          </div>
          <div v-if="question.question.question_type === 'multi_line'">
            <label class="block text-sm font-medium mb-1">{{ question.question.title }}<span v-if="question.required" class="text-red-400 ml-0.5">*</span></label>
            <textarea :name="question.id" rows="4"
              :placeholder="question.question.question_details?.placeholder || question.question.title"
              :required="question.required"
              :minlength="question.question.question_details?.min_length || undefined"
              :maxlength="question.question.question_details?.max_length || undefined"
              class="w-full border-b border-gray-300 py-2 text-sm focus:outline-none focus:border-blue-500 bg-transparent resize-none"></textarea>
            <p v-if="question.question.question_details?.min_length" class="text-[11px] text-gray-400 mt-0.5">
              Minimum {{ question.question.question_details.min_length }} characters
            </p>
          </div>
          
          <div v-if="question.question.question_type === 'drop_down' && question.question.question_details.options.length">
            <TimelydoDropdown
              :title="question.question.title"
              :name="question.id"
              :selected_value="question.question.question_details.options[0]"
              :values="formatOptions(question.question.question_details.options)"
              :opts="dropdownOpts(question.question.question_details.options)"
            >
            </TimelydoDropdown>
          </div>

          <div v-if="question.question.question_type === 'radio_button' && question.question.question_details.options.length">
            <p>{{ question.question.title }}</p>
            <div v-for="option in question.question.question_details.options" :key="option" class="ml-2">
              <input 
                type="radio" 
                :id="question.id + '_' + option"
                :name="question.id"
                :value="option" 
                :required="question.required"
                class="focus:outline-none focus:ring-0 border-[0.125rem] border-neutral-300 mb-1"  
              />
              <label :for="question.id + '_' + option" class="ml-2 opacity-80">{{ option }}</label>
            </div>
          </div>

          <div v-if="question.question.question_type === 'check_box' && question.question.question_details.options.length">
            <p>{{ question.question.title }}</p>
            <div v-for="(option, index) in question.question.question_details.options" :key="option" class="mt-1 ml-2">
              <input 
                type="checkbox" 
                :id="question.id + '_' + option"
                :name="question.id + '[]'"
                :value="option" 
                :required="index === 0 && question.required"
                @change="handleCheckboxChange(question)"
                class=" relative float-left mr-[6px] mt-[0.15rem] h-[1.125rem] w-[1.125rem] appearance-none rounded-[0.25rem] border-[0.125rem] border-solid focus:ring-transparent focus:outline-0 checked:shadow-none border-neutral-300 outline-none before:pointer-events-none before:absolute before:h-[0.875rem] before:w-[0.875rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_13px_transparent] before:content-[\'\'] checked:border-primary checked:bg-primary checked:before:opacity-[0.16] checked:after:absolute checked:after:-mt-px checked:after:ml-[0.25rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.375rem] checked:after:rotate-45 checked:after:border-[0.125rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[\'\'] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:shadow-none focus:transition-[border-color_0.2s] focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:z-[1] focus:after:block focus:after:h-[0.875rem] focus:after:w-[0.875rem] focus:after:rounded-[0.125rem] focus:after:content-[\'\'] checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:after:-mt-px checked:focus:after:ml-[0.25rem] checked:focus:after:h-[0.8125rem] checked:focus:after:w-[0.375rem] checked:focus:after:rotate-45 checked:focus:after:rounded-none checked:focus:after:border-[0.125rem] checked:focus:after:border-l-0 checked:focus:after:border-t-0 checked:focus:after:border-solid checked:focus:after:border-white checked:focus:after:bg-transparent dark:checked:border-primary dark:checked:bg-primary dark:focus:before:shadow-[0px_0px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[0px_0px_0px_13px_#3b71ca]" 
              />
              <label :for="question.id + '_' + option" class="ml-1 opacity-80">{{ option }}</label>
            </div>
          </div>
        </div>
    </div>
    
</template>

<script>
import Constants from "controllers/vue_components/mixins/constants"
import FlashNotifications from "controllers/vue_components/flash_toast"
import RequestMixin from "controllers/vue_components/mixins/request"
import TimelydoInput from "controllers/vue_components/fields/timelydo_input";
import TimelydoDropdown from "controllers/vue_components/fields/timelydo_dropdown"
import TimelydoTextArea from "controllers/vue_components/fields/timelydo_textarea"
export default {
    name: 'PublicEventQuestions',
    props: [],
    emits: [],
    mixins: [RequestMixin, Constants],
    components: {
        FlashNotifications, TimelydoInput, TimelydoDropdown, TimelydoTextArea
    },
    data() {
        return {
            active_questions: [],
            ratings: {},
            uploadedFiles: {},
            uploadErrors: {},
        };
    },
    methods: {
        updateData(data){
            this.active_questions = data
        },
        formatOptions(options) {
            return options.map(option => {
                return { value: option, key: option };
            });
        },
        dropdownOpts(options) {
            if (options.length > 5) {
                return {
                    search_able: true,
                    maximum_height: '190px'
                };
            }
            return {};
        },
        setRating(questionId, value) {
            this.ratings = { ...this.ratings, [questionId]: value };
        },
        buildAccept(details) {
            const types = details && details.allowed_types;
            if (!types || !types.length) return undefined;
            const MIME = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
                           doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                           csv: 'text/csv', mp4: 'video/mp4', gif: 'image/gif', webp: 'image/webp', txt: 'text/plain' };
            return types.map(t => MIME[t] || `.${t}`).join(',');
        },
        async handleFileUpload(e, questionId, details) {
            const file = e.target.files && e.target.files[0];
            if (!file) return;
            const maxBytes = ((details && details.max_size_mb) || 50) * 1024 * 1024;
            if (file.size > maxBytes) {
                this.uploadErrors = { ...this.uploadErrors, [questionId]: `File exceeds the ${(details && details.max_size_mb) || 50} MB limit` };
                return;
            }
            const body = new FormData();
            body.append('file', file);
            try {
                const res = await fetch('/api/v1/public/uploads', { method: 'POST', body });
                const json = await res.json();
                if (json.success) {
                    this.uploadedFiles = { ...this.uploadedFiles, [questionId]: json.data.signed_id };
                    this.uploadErrors = { ...this.uploadErrors, [questionId]: null };
                } else {
                    this.uploadErrors = { ...this.uploadErrors, [questionId]: json.message || 'Upload failed' };
                }
            } catch {
                this.uploadErrors = { ...this.uploadErrors, [questionId]: 'Upload failed. Please try again.' };
            }
        },
        handleCheckboxChange(question) {
          const checkboxes = document.querySelectorAll(`input[name="${question.id}[]"]`);
          const anyChecked = Array.from(checkboxes).some(checkbox => checkbox.checked);
          checkboxes.forEach((checkbox, index) => {
            if (index === 0) {
              checkbox.required = !anyChecked && question.required;
            } else {
              checkbox.required = false;
            }
          });
        }
    },

    async created() {
    }
}
</script>
